import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import type { Translations } from './translations/pt-BR';
import ptBR from './translations/pt-BR';
import en   from './translations/en';
import es   from './translations/es';
import fr   from './translations/fr';
import ptPT from './translations/pt-PT';

// ─── Types ────────────────────────────────────────────────────────────────────

export type LangCode = 'pt-BR' | 'pt-PT' | 'es' | 'en' | 'fr';

export const SUPPORTED_LANGUAGES: { code: LangCode; label: string; flag: string }[] = [
  { code: 'pt-BR', label: 'Português (Brasil)',  flag: '🇧🇷' },
  { code: 'pt-PT', label: 'Português (Portugal)', flag: '🇵🇹' },
  { code: 'es',    label: 'Español',              flag: '🇪🇸' },
  { code: 'en',    label: 'English',              flag: '🇬🇧' },
  { code: 'fr',    label: 'Français',             flag: '🇫🇷' },
];

const TRANSLATIONS: Record<LangCode, Translations> = {
  'pt-BR': ptBR,
  'pt-PT': ptPT,
  'es':    es,
  'en':    en,
  'fr':    fr,
};

// ─── Translation helper ───────────────────────────────────────────────────────

/**
 * Resolve a dot-path key like "nav.dashboard" against a translations object.
 * Supports {var} interpolation.
 */
export function resolveKey(translations: Translations, key: string, vars?: Record<string, string>): string {
  const parts = key.split('.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let value: any = translations;
  for (const part of parts) {
    if (value == null || typeof value !== 'object') return key;
    value = value[part];
  }
  if (typeof value !== 'string') return key;
  if (!vars) return value;
  return value.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
}

// ─── Initial language detection ───────────────────────────────────────────────

function detectInitialLang(): LangCode {
  // 1. Persisted preference (user explicitly chose a language)
  try {
    const stored = localStorage.getItem('professya_lang');
    if (stored && stored in TRANSLATIONS) return stored as LangCode;
  } catch { /* ignore */ }

  // 2. Default: pt-BR
  return 'pt-BR';
}

// ─── Context ──────────────────────────────────────────────────────────────────

interface I18nContextValue {
  lang:    LangCode;
  setLang: (code: LangCode) => void;
  t:       (key: string, vars?: Record<string, string>) => string;
}

export const I18nContext = createContext<I18nContextValue>({
  lang:    'pt-BR',
  setLang: () => {},
  t:       (key) => key,
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<LangCode>(detectInitialLang);

  function setLang(code: LangCode) {
    setLangState(code);
    try { localStorage.setItem('professya_lang', code); } catch { /* ignore */ }
  }

  // Sync document lang attribute and safeInvoke currentLang
  useEffect(() => {
    document.documentElement.lang = lang;
    // Lazy import to avoid circular dependency
    import('../safeInvoke').then(m => m.setCurrentLang(lang)).catch(() => {});
  }, [lang]);

  const value = useMemo<I18nContextValue>(() => ({
    lang,
    setLang,
    t: (key, vars) => resolveKey(TRANSLATIONS[lang] ?? ptBR, key, vars),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [lang]);

  return React.createElement(I18nContext.Provider, { value }, children);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}
